import { Redirect, router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    FullScreenSpinner,
    ScreenContainer,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../../../common/hooks/useSendEmailToSupport';
import { mapAccountColorToCardColor } from '../../../../common/utils/bankingUtils';
import { captureException } from '../../../../logger/Logger';
import useReissueCard from '../../../hooks/card/useReissueCard';
import useGetAccountDisplayDetailsFromGlobalState from '../../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import useGetAllBankAccountDetails from '../../../hooks/useGetAllBankAccountDetails';
import { CardForm, UserReissueReason } from '../../../types/Card';
import Address from './steps/Address';
import CardLocation from './steps/CardLocation';
import CardSent from './steps/CardSent';
import DifferentAddress from './steps/DifferentAddress';
import ReplacementReason from './steps/ReplacementReason';
import TerminateCard from './steps/TerminateCard';

enum ReplaceCardSteps {
    CARD_LOCATION = 'cardLocation',
    REPLACEMENT_REASON = 'replacementReason',
    ADDRESS = 'address',
    REQUEST_NEW_ADDRESS = 'requestNewAddress',
    TERMINATE_CARD = 'terminateCard',
    CARD_SENT_CONFIRMATION = 'cardSentConfirmation',
}

export interface ReissueOption {
    value: UserReissueReason;
    description: string;
}

const HAS_CARD_REPLACEMENT_REASONS = [
    {
        value: UserReissueReason.Damaged,
        description: "It's damaged and not working",
    },
    {
        value: UserReissueReason.Appearance,
        description: "It's damaged but still works",
    },
];

const NO_CARD_REPLACEMENT_REASONS = [
    { value: UserReissueReason.Lost, description: "It's lost" },
    { value: UserReissueReason.Stolen, description: 'It was stolen' },
];

const ReplaceCard = () => {
    const { syncteraAccountId, cardId } = useLocalSearchParams<{
        syncteraAccountId: string;
        cardId: string;
    }>();

    const [index, setIndex] = useState(0);
    const [tabHistoryStack, setTabHistoryStack] = useState<number[]>([0]);
    const [isCardLostOrStolen, setIsCardLostOrStolen] = useState<
        boolean | null
    >(null);
    const [validReplacementReasons, setValidReplacementReasons] = useState<
        ReissueOption[] | null
    >(null);
    const [replacementReason, setReplacementReason] =
        useState<UserReissueReason | null>(null);
    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });
    const toast = useToastController();
    const sendEmailToSupport = useSendEmailToSupport();

    const goToPage = (nextTab: number) => {
        setIndex(nextTab);
        setTabHistoryStack((prevState) => {
            return [...prevState, nextTab];
        });
    };

    const onPressBack = () => {
        if (index === cardSentIndex) {
            router.navigate({
                pathname: '/banking/card/view-card',
                params: {
                    syncteraAccountId,
                    cardId,
                },
            });
        } else if (tabHistoryStack.length > 1) {
            setIndex(
                tabHistoryStack[tabHistoryStack.length - 2] ??
                    cardLocationIndex,
            );
            setTabHistoryStack((prevState) => {
                return prevState.filter((i) => i !== index);
            });
        } else {
            if (router.canGoBack()) {
                router.back();
            } else {
                router.replace('/banking/card/manage-card');
            }
        }
    };

    const { refetchAccountDetails, isLoading } =
        useGetAllBankAccountDetails(false);

    const { mutateAsync: reissueCreditCard, isPending } = useReissueCard();

    if (syncteraAccountId == null) {
        captureException(
            new Error('Account id not found for reissue card page'),
            { cardId },
        );
        return <Redirect href="/home/banking" />;
    }

    if (account == null || isLoading || isPending) {
        return <FullScreenSpinner />;
    }

    const card = account.cards?.find((card) => card.id === cardId);

    if (card == null) {
        captureException(new Error('Card not found for replace card page'), {
            cardId,
        });
        return <Redirect href="/home/banking" />;
    }

    const routes = Object.values(ReplaceCardSteps).map((step) => ({
        key: step,
    }));

    const getRouteIndex = (lockStep: ReplaceCardSteps) =>
        routes.findIndex(({ key }) => key === lockStep);

    const cardLocationIndex = getRouteIndex(ReplaceCardSteps.CARD_LOCATION);
    const replacementReasonIndex = getRouteIndex(
        ReplaceCardSteps.REPLACEMENT_REASON,
    );
    const addressIndex = getRouteIndex(ReplaceCardSteps.ADDRESS);
    const differentAddressIndex = getRouteIndex(
        ReplaceCardSteps.REQUEST_NEW_ADDRESS,
    );
    const terminateCardIndex = getRouteIndex(ReplaceCardSteps.TERMINATE_CARD);
    const cardSentIndex = getRouteIndex(
        ReplaceCardSteps.CARD_SENT_CONFIRMATION,
    );

    const handleReissueCreditCard = async () => {
        try {
            await reissueCreditCard({
                body: {
                    form: card.form,
                    syncteraAccountId:
                        account.chargeSecuredSyncteraAccountId as string,
                    embossName: card.embossName.line1,
                    reissuedFromId: card.id,
                    reissueReason: replacementReason as UserReissueReason,
                    ...(card.form === CardForm.VIRTUAL
                        ? {
                              cardColor: mapAccountColorToCardColor(
                                  account.color,
                              ),
                          }
                        : {}),
                },
            });
            await refetchAccountDetails();
            goToPage(cardSentIndex);
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong.');
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: ReplaceCardSteps } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const Content = (() => {
            switch (route.key) {
                case ReplaceCardSteps.CARD_LOCATION:
                    return (
                        <CardLocation
                            lostOrStolen={isCardLostOrStolen}
                            onSelect={(
                                isCardLostOrStolenSelection: boolean,
                            ) => {
                                setIsCardLostOrStolen(
                                    isCardLostOrStolenSelection,
                                );
                                setValidReplacementReasons(
                                    isCardLostOrStolenSelection
                                        ? NO_CARD_REPLACEMENT_REASONS
                                        : HAS_CARD_REPLACEMENT_REASONS,
                                );
                            }}
                            onPress={() => {
                                goToPage(replacementReasonIndex);
                            }}
                        />
                    );
                case ReplaceCardSteps.REPLACEMENT_REASON:
                    return (
                        <ReplacementReason
                            lostOrStolen={isCardLostOrStolen as boolean}
                            replacementReason={replacementReason}
                            replacementOptions={
                                validReplacementReasons as ReissueOption[]
                            }
                            onSelect={(replacementReason) => {
                                setReplacementReason(
                                    replacementReason as UserReissueReason,
                                );
                            }}
                            onPress={() => {
                                goToPage(addressIndex);
                            }}
                        />
                    );
                case ReplaceCardSteps.ADDRESS:
                    return (
                        <Address
                            embossName={card.embossName.line1}
                            lostOrStolen={isCardLostOrStolen as boolean}
                            onPress={async () => {
                                if (isCardLostOrStolen) {
                                    goToPage(terminateCardIndex);
                                } else {
                                    await handleReissueCreditCard();
                                }
                            }}
                            onPressUseNewAddress={() => {
                                goToPage(differentAddressIndex);
                            }}
                        />
                    );
                case ReplaceCardSteps.REQUEST_NEW_ADDRESS:
                    return (
                        <DifferentAddress
                            onPress={() => {
                                void sendEmailToSupport({
                                    subject: 'Replacement card',
                                });
                            }}
                        />
                    );
                case ReplaceCardSteps.TERMINATE_CARD:
                    return (
                        <TerminateCard
                            card={card}
                            onPress={handleReissueCreditCard}
                        />
                    );
                case ReplaceCardSteps.CARD_SENT_CONFIRMATION:
                    return (
                        <CardSent
                            lostOrStolen={isCardLostOrStolen as boolean}
                            onPress={() => {
                                router.navigate({
                                    pathname: '/banking/card/view-card',
                                    params: {
                                        syncteraAccountId,
                                        cardId,
                                    },
                                });
                            }}
                        />
                    );
                default:
                    return <></>;
            }
        })();

        return <YStack flex={1}>{Content}</YStack>;
    };

    const isOnCardSentScreen = index === cardSentIndex;

    return (
        <ScreenContainer
            title="Replace physical card"
            hasBackButton={!isOnCardSentScreen}
            hasCloseButton={isOnCardSentScreen}
            onBack={onPressBack}
            onClose={onPressBack}
        >
            <View height="100%">
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    onIndexChange={setIndex}
                    renderScene={renderScene}
                    renderTabBar={() => <></>}
                />
            </View>
        </ScreenContainer>
    );
};

export default ReplaceCard;
